import { Suspense, useState } from 'react'
import clsx from 'clsx'
import { useMediaQuery } from '@mantine/hooks'
import { ActionIcon, AspectRatio, Skeleton, Image, Avatar } from '@mantine/core'
import { IconReplace, IconStar } from '@tabler/icons-react'
import { Typography } from '../../common/typography'
import { CompetitionTeam } from '../../performance/api'
import Stat from '../Stat/Stat'
import ComparePickerComponent from './ComparePickerComponent'
import classes from './CompareCard.module.css'
import { CompareSubject } from '../../compare/Compare'
import { usePlayerRanks, useTeamRanks } from '../../ranks/api'
import { useCompetitionStats } from '../../performance/api'

export default function CompareCard({
  color,
  backgroundColor,
  direction,
  opened,
  open,
  close,
  teamWithPlayers,
  subject,
  setSubject,
  competitionId,
}: {
  color: string
  backgroundColor: string
  direction: 'left' | 'right'
  opened: boolean
  open: () => void
  close: () => void
  teamWithPlayers: CompetitionTeam[]
  subject: CompareSubject
  setSubject: React.Dispatch<React.SetStateAction<CompareSubject>>
  competitionId: number
}) {
  const isDesktop = useMediaQuery('(min-width: 800px)')
  const [subjectImageLoaded, setSubjectImageLoaded] = useState<boolean>(true)
  const [subjectTeamImageLoaded, setSubjectTeamImageLoaded] =
    useState<boolean>(false)

  return (
    <>
      <div className={classes.compareCard}>
        <ActionIcon
          variant="filled"
          className={classes.compareButton}
          onClick={open}
        >
          <IconReplace className={classes.compareIcon} />
        </ActionIcon>
        <div
          className={clsx(classes.patternContainer, {
            [classes.patterContainerMobile]: !isDesktop,
          })}
          style={
            { '--backgroundColor': backgroundColor } as React.CSSProperties
          }
        >
          <div
            className={clsx(
              { [classes.patternLeft]: direction === 'left' },
              { [classes.patternRight]: direction === 'right' }
            )}
          />
          <div
            className={clsx(
              { [classes.patternLeft]: direction === 'left' },
              { [classes.patternRight]: direction === 'right' }
            )}
          />
        </div>
        <div className={classes.imageWrapper}>
          <AspectRatio
            ratio={1 / 1}
            className={classes.aspectRatio}
            style={{ '--color': color }}
          >
            {!subjectImageLoaded && <Skeleton circle height="100%" />}
            {subject && subject.photo ? (
              <Image
                src={subject.photo}
                alt={subject.name}
                className={classes.subjectImage}
                onLoad={() => setSubjectImageLoaded(true)}
                data-loaded={subjectImageLoaded}
                fit="scale-down"
              />
            ) : (
              <Avatar className={classes.avatar} style={{ '--color': color }} />
            )}

            {subject?.type === 'user' && (
              <div className={classes.teamLogoContainer}>
                {!subjectTeamImageLoaded && <Skeleton circle height="100%" />}

                <Image
                  src={subject.team_logo}
                  alt={subject.name}
                  className={classes.subjectImageTeam}
                  onLoad={() => setSubjectTeamImageLoaded(true)}
                  data-loaded={subjectTeamImageLoaded}
                  fit="scale-down"
                />
              </div>
            )}
          </AspectRatio>
        </div>
        <Typography
          fc={{ light: 'gray-7', dark: 'gray-3' }}
          lh="150"
          ta="center"
        >
          {subject?.name}
        </Typography>

        <div
          className={clsx(classes.stats, {
            [classes.statsMobile]: !isDesktop,
          })}
        >
          <Suspense
            fallback={
              <>
                <Skeleton
                  className={clsx(classes.skeleton, {
                    [classes.skeletonMobile]: !isDesktop,
                  })}
                />
                <Skeleton
                  className={clsx(classes.skeleton, {
                    [classes.skeletonMobile]: !isDesktop,
                  })}
                />
              </>
            }
          >
            {subject?.type === 'team' && (
              <TeamStats teamId={subject.id} competitionId={competitionId} />
            )}
            {subject?.type === 'user' && (
              <PlayerStats
                playerId={subject.player_id}
                competitionId={competitionId}
              />
            )}
            {subject?.type === 'overall' && (
              <OverallStats competitionId={competitionId} />
            )}
          </Suspense>
        </div>
      </div>
      <ComparePickerComponent
        opened={opened}
        close={close}
        teamWithPlayers={teamWithPlayers}
        subject={subject}
        setSubject={setSubject}
      />
    </>
  )
}

function TeamStats({
  teamId,
  competitionId,
}: {
  teamId: number
  competitionId: number
}) {
  const { data: teamRanks } = useTeamRanks({
    competition_id: competitionId,
    team_id: teamId,
  })

  const teamRank = teamRanks.items[0]

  if (!teamRank) {
    return null
  }

  return (
    <>
      <Stat
        title="Overall Rank"
        value={teamRank.rank}
        isValueSub={true}
        background="gray"
      />
      <Stat
        title="Rating"
        value={teamRank.rating.toFixed(2)}
        isValueSub={false}
        icon={IconStar}
        background="gray"
      />
    </>
  )
}

function PlayerStats({
  playerId,
  competitionId,
}: {
  playerId: number
  competitionId: number
}) {
  const { data: playerRanks } = usePlayerRanks({
    competition_id: competitionId,
    player_id: playerId,
  })

  const playerRank = playerRanks.items[0]

  if (!playerRank) {
    return null
  }

  return (
    <>
      <Stat
        title="Overall Rank"
        value={playerRank.rank}
        isValueSub={true}
        background="gray"
      />
      <Stat
        title="Rating"
        value={playerRank.rating.toFixed(2)}
        isValueSub={false}
        icon={IconStar}
        background="gray"
      />
    </>
  )
}

function OverallStats({ competitionId }: { competitionId: number }) {
  const { data: stats } = useCompetitionStats({
    competition_id: competitionId,
  })

  return (
    <>
      <Stat title="League" value="-" isValueSub={true} background="gray" />
      <Stat
        title="Rating"
        value={stats.overall_rating ? stats.overall_rating.toFixed(2) : '-'}
        isValueSub={false}
        icon={IconStar}
        background="gray"
      />
    </>
  )
}
